import { useMemo } from 'react';
import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudListPage } from '@core/components';
import { useCrudList } from '@core/hooks/useCrudList';
import {
  type Color,
  deleteColor,
  getAllColors,
  reorderColors,
} from '../../../api/color';
import ColorDialog from '../../components/ColorDialog';
import { useAppContext } from '../../context/AppContext';

const Colors = () => {
  const { refreshContext } = useAppContext();
  const {
    items: colors,
    loading,
    dialogOpen,
    editingItem: editingColor,
    deleteConfirm,
    load: loadColors,
    openCreate,
    closeDialog,
    cancelDelete,
    confirmDelete,
    getRowId,
    contextMenuItems,
    rowReorder,
  } = useCrudList<Color>({
    loadItems: getAllColors,
    deleteItem: (color) => deleteColor(color.id),
    reorderItems: reorderColors,
    loadErrorMessage: 'Failed to load colors',
    deleteSuccessMessage: 'Color deleted',
    deleteErrorMessage: 'Failed to delete color',
    reorderErrorMessage: 'Failed to reorder colors',
    refreshContext,
  });

  const columns = useMemo<GridColDef<Color>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'color',
        headerName: 'Color',
        width: 160,
        renderCell: ({ row }) => {
          const hexCode = row.hexCode?.trim();
          return hexCode ? (
            <Box
              sx={{
                width: 100,
                height: 24,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: hexCode,
              }}
            />
          ) : null;
        },
      },
    ],
    []
  );

  return (
    <CrudListPage<Color>
      variant="section"
      title="Colors"
      rows={colors}
      columns={columns}
      loading={loading}
      getRowId={getRowId}
      contextMenuItems={contextMenuItems}
      rowReorder={rowReorder}
      addConfig={{ label: 'Add Color', onAdd: openCreate }}
      addEditDialog={
        <ColorDialog
          open={dialogOpen}
          onClose={closeDialog}
          color={editingColor}
          onSuccess={() => loadColors()}
        />
      }
      deleteConfig={{
        title: 'Delete color',
        item: deleteConfirm,
        getContent: (color) =>
          `Are you sure you want to delete "${color.name}"?`,
        onConfirm: confirmDelete,
        onCancel: cancelDelete,
      }}
    />
  );
};

export default Colors;
